import AuthPageFrame from '../AuthPageFrame';
import { Button } from '@/components/ui/button';
import { FormEvent, useRef, useState } from 'react';
import { getErrorMessage } from '@/shared/lib/getErrorMessage';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import { RecoveryRequestError, requestPasswordReset } from '../../api/passwordRecovery';
import { useCountdown } from '@/shared/hooks/useCountdown';

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isAccepted, setIsAccepted] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const isPendingRef = useRef(false);
  const { secondsLeft, startCountdown } = useCountdown();

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (isPendingRef.current || secondsLeft) return;
    if (email.trim().length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Введите корректную электронную почту.');
      return;
    }
    isPendingRef.current = true;
    setIsPending(true);
    setError('');
    try {
      const result = await requestPasswordReset({ email: email.trim() });
      setIsAccepted(true);
      startCountdown(result.retry_after_seconds);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      if (requestError instanceof RecoveryRequestError && requestError.retryAfterSeconds) {
        startCountdown(requestError.retryAfterSeconds);
      }
    } finally {
      isPendingRef.current = false;
      setIsPending(false);
    }
  };

  return (
    <AuthPageFrame subtitle="Восстановление пароля">
      <form
        className="space-y-6"
        noValidate
        onSubmit={onSubmit}
      >
        <p className="text-sm leading-5 text-muted-foreground" aria-live="polite">
          {isAccepted
            ? 'Если для этого адреса есть аккаунт, мы отправим письмо со ссылкой для восстановления пароля.'
            : 'Введите почту, указанную при регистрации. Мы отправим ссылку для восстановления пароля.'}
        </p>
        <div className="space-y-2">
          <Label className="text-sm font-normal leading-5 text-muted-foreground" htmlFor="recovery-email">
            Электронная почта
          </Label>
          <Input
            autoComplete="email"
            disabled={isPending || isAccepted}
            id="recovery-email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@company.ru"
            required
            type="email"
            value={email}
          />
        </div>
        {error && <p className="text-sm text-[hsl(var(--error-text))]" role="alert">{error}</p>}
        <Button
          className="w-full"
          disabled={isPending || secondsLeft > 0}
          loading={isPending}
          size="lg"
          type="submit"
        >
          {secondsLeft ? `Повторить через ${secondsLeft} сек` : isAccepted ? 'Отправить повторно' : 'Отправить ссылку'}
        </Button>
        {isAccepted && (
          <Button
            className="w-full"
            onClick={() => { setIsAccepted(false); setError(''); }}
            type="button"
            variant="tertiary"
          >
            Другой адрес
          </Button>
        )}
        <Button
          className="w-full"
          asChild
          variant="tertiary"
        >
          <Link to="/login">Вернуться ко входу</Link>
        </Button>
      </form>
    </AuthPageFrame>
  );
};

export default ForgotPasswordForm;